import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Loader2, RefreshCw, Copy, Reply, AlertTriangle } from 'lucide-react';
import { loadTeamSenders } from './SenderSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-domains`;
const inp = 'bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400';
const btn = 'inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] text-xs font-semibold rounded-md px-3 py-2 disabled:opacity-60';
const tone = (st) => (st === 'verified' ? 'bg-emerald-100 text-emerald-700' : st === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-500/15 text-amber-700');
const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

export const StatusPill = ({ status, testid }) => (
  <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${tone(status)}`} data-testid={testid}>
    {status === 'verified' ? 'Verified' : status === 'failed' ? 'Failed' : 'Pending'}</span>
);

const Records = ({ d }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs text-left" data-testid={`domain-records-${d.name}`}>
      <thead className="text-slate-500"><tr><th className="py-1 pr-2">Type</th><th className="pr-2">Name / host</th><th className="pr-2">Value</th><th className="pr-2">Priority</th><th>Status</th></tr></thead>
      <tbody>
        {d.records.map((r, i) => (
          <tr key={i} className="border-t border-slate-200 text-slate-700 align-top">
            <td className="py-1.5 pr-2 font-mono">{r.type}</td>
            <td className="pr-2 font-mono break-all"><button onClick={() => copy(r.name)} className="hover:text-[#00A79D] text-left" data-testid={`copy-record-name-${i}`}>{r.name} <Copy className="inline w-3 h-3" /></button></td>
            <td className="pr-2 font-mono break-all max-w-[340px]"><button onClick={() => copy(r.value)} className="hover:text-[#00A79D] text-left" data-testid={`copy-record-value-${i}`}>{r.value} <Copy className="inline w-3 h-3" /></button></td>
            <td className="pr-2">{r.priority ?? '-'}</td>
            <td><StatusPill status={r.status} testid={`record-status-${i}`} /></td>
          </tr>))}
      </tbody>
    </table>
  </div>
);

const ReplyTracking = ({ rt, webhook, onSaved }) => {
  const [f, setF] = useState(rt);
  const [busy, setBusy] = useState(false);
  const save = async (enabled) => {
    setBusy(true);
    try { const r = await axios.put(`${API}/reply-tracking`, { ...f, enabled }); setF(r.data); onSaved(); toast.success(enabled ? 'Reply tracking on' : 'Reply tracking off'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="reply-tracking-section">
      <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Reply className="w-4 h-4 text-[#00A79D]" /> Reply tracking for 1:1 emails
        <StatusPill status={f.enabled ? 'verified' : 'pending'} testid="reply-tracking-status" /></p>
      <p className="text-xs text-slate-500">Each 1:1 email gets its own reply address. Replies are logged on the contact card and forwarded to the sender's inbox.
        In Resend: Receiving, copy your receiving domain (your abcd.resend.app address, or a domain above with Receiving enabled, e.g. reply.caretechcompare.co.uk) and add the <span className="font-mono">email.received</span> event to the webhook below.</p>
      <button onClick={() => copy(webhook)} className="text-[11px] font-mono text-slate-600 hover:text-[#00A79D] break-all text-left" data-testid="reply-webhook-url">{webhook} <Copy className="inline w-3 h-3" /></button>
      <div className="flex flex-wrap gap-2">
        <input value={f.inbound_domain || ''} onChange={(e) => setF({ ...f, inbound_domain: e.target.value })} placeholder="Receiving domain, e.g. abcd.resend.app" className={`${inp} flex-1 min-w-[220px]`} data-testid="reply-inbound-domain-input" />
        <button onClick={() => save(true)} disabled={busy} className={btn} data-testid="reply-tracking-enable-btn">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{f.enabled ? 'Save' : 'Turn on'}</button>
        {f.enabled && <button onClick={() => save(false)} disabled={busy} className="text-xs text-slate-500 hover:text-red-400 px-2" data-testid="reply-tracking-disable-btn">Turn off</button>}
      </div>
    </div>
  );
};

// Settings > Sending domains (admins): add caretechcompare.co.uk to Resend, show DNS records + live status, reply tracking.
export default function EmailDomainsPanel() {
  const [d, setD] = useState(null);
  const [name, setName] = useState('caretechcompare.co.uk');
  const [busy, setBusy] = useState('');
  const load = () => axios.get(API).then((r) => setD(r.data)).catch((e) => setD({ items: [], error: e.response?.data?.detail || 'Could not load', reply_tracking: { enabled: false, inbound_domain: '' }, webhook_url: '' }));
  useEffect(() => { load(); }, []);
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); await load(); loadTeamSenders(true); if (ok) toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); }
    finally { setBusy(''); }
  };
  if (!d) return <Loader2 className="w-4 h-4 animate-spin text-slate-500" />;
  const has = d.items.some((x) => x.name === name.trim().toLowerCase());
  return (
    <div className="space-y-3 p-4 bg-white rounded-lg border border-slate-200" data-testid="email-domains-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Globe className="w-4 h-4 text-[#00A79D]" /> Sending domains</h3>
        <button onClick={() => act('refresh', () => Promise.resolve())} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="domains-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${busy === 'refresh' ? 'animate-spin' : ''}`} /> Refresh status</button>
      </div>
      <p className="text-xs text-slate-500">Add a domain, copy each DNS record into your domain host, then press Verify. Once verified, anyone can pick an address on it as their sending address in their signature.</p>
      {d.error && <p className="text-xs text-amber-700 flex gap-1.5" data-testid="domains-error"><AlertTriangle className="w-4 h-4 shrink-0" />{d.error}</p>}
      {!has && (
        <div className="flex flex-wrap gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} className={`${inp} flex-1 min-w-[220px]`} data-testid="domain-name-input" />
          <button onClick={() => act('add', () => axios.post(API, { name }), 'Domain added. Copy the DNS records below.')} disabled={!!busy} className={btn} data-testid="domain-add-btn">
            {busy === 'add' && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Add domain</button>
        </div>)}
      {d.items.map((x) => <DomainCard key={x.id} x={x} busy={busy} onVerify={() => act(x.id, () => axios.post(`${API}/${x.id}/verify`), 'Verification started. DNS can take a while, refresh shortly.')} />)}
      <ReplyTracking rt={d.reply_tracking} webhook={d.webhook_url} onSaved={load} />
    </div>
  );
}

const DomainCard = ({ x, busy, onVerify }) => {
  const [open, setOpen] = useState(x.status !== 'verified');
  const cap = x.capabilities || {};
  return (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`domain-card-${x.name}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 flex flex-wrap items-center gap-2">{x.name} <StatusPill status={x.status} testid={`domain-status-${x.name}`} />
              <span className="text-[10px] text-slate-500 font-normal" data-testid={`domain-caps-${x.name}`}>Sending {cap.sending || 'n/a'} · Receiving {cap.receiving || 'n/a'}</span></p>
            <div className="flex items-center gap-2">
              <button onClick={() => setOpen(!open)} className="text-xs text-slate-500 hover:text-slate-900" data-testid={`domain-records-toggle-${x.name}`}>{open ? 'Hide' : 'Show'} DNS records</button>
              {x.status !== 'verified' && <button onClick={onVerify} disabled={!!busy} className={btn} data-testid={`domain-verify-btn-${x.name}`}>
                {busy === x.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Verify</button>}
            </div>
          </div>
          {open && <Records d={x} />}
        </div>
  );
};
